'use strict';

/**
 * NodeWorkspace: the single-node detail screen, built on pict-section-workspace (Work Item #465, B5f).
 *
 * A NodeRegistration is a small enough thing to deserve a focused screen rather than the generic entity detail:
 * a status pill, an overview of its identity and capability, and the lifecycle actions (Approve / Suspend /
 * Resume / Rotate token / Revoke) as header buttons that gate on the current Status. Create mode hosts Provision
 * -- name a new login-less agent, or reassign an existing one -- and reveals the node token exactly once.
 *
 * The Nodes LIST (EntityDescriptors.NodeRegistration) stays the entry point; a row-click opens this workspace.
 * Retire (deleting an agent persona) lives on the Team roster, not here, because a retirable agent has no node.
 * This is the node UI "workspace" (a screen); it is unrelated to plansheet's Workspace ENTITY (repositories).
 *
 * The base provider's REST helpers assume a Fable RestClient callback shape plansheet does not use, so this
 * subclass overrides _getJSON/_postJSON/_putJSON to ride the recovery-covered FrameworkClient (401 re-auth).
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libWorkspace = require('pict-section-workspace');
const libFrameworkClient = require('../providers/FrameworkClient.js');

// CapabilityJSON summaries, kept local (EntityDescriptors' equivalents are not exported). Tolerant of malformed JSON.
function _cap(pRecord)
{
	try { return JSON.parse((pRecord && pRecord.CapabilityJSON) || '{}') || {}; }
	catch (pError) { return {}; }
}
function _rolesSummary(pRecord) { let tmpCap = _cap(pRecord); return (Array.isArray(tmpCap.Roles) ? tmpCap.Roles : []).join(' + ') || '-'; }
function _workspacesSummary(pRecord) { let tmpCap = _cap(pRecord); return (Array.isArray(tmpCap.Workspaces) ? tmpCap.Workspaces : []).join(', ') || '-'; }
// The client version the node reported at connect (CapabilityJSON.Version, V51 F153), and the capability actions it
// advertises to the hub -- what makes it a candidate on the Capabilities screen. Both '-' until the node reports them.
function _versionSummary(pRecord) { let tmpCap = _cap(pRecord); return String(tmpCap.Version || '').trim() || '-'; }
// The probed hardware the node reported (CapabilityJSON.Hardware, V51 F155, WI-513), as a one-line summary. '-'
// until a node on the readiness-aware client reports it. Fields it could not measure are omitted rather than shown
// as zero, so a GPU-less box reads "RAM 32768 MB, 480000 MB disk free" with no GPU clause.
function _hardwareSummary(pRecord)
{
	let tmpCap = _cap(pRecord);
	let tmpHardware = (tmpCap.Hardware && typeof tmpCap.Hardware === 'object') ? tmpCap.Hardware : null;
	if (!tmpHardware) { return '-'; }
	let tmpParts = [];
	let tmpRAM = (tmpHardware.RAMMB && typeof tmpHardware.RAMMB === 'object') ? tmpHardware.RAMMB : {};
	let tmpGPU = (tmpHardware.GPU && typeof tmpHardware.GPU === 'object') ? tmpHardware.GPU : {};
	if (tmpRAM.Total !== null && tmpRAM.Total !== undefined) { tmpParts.push('RAM ' + tmpRAM.Total + ' MB'); }
	if (tmpGPU.Present)
	{
		let tmpCards = Array.isArray(tmpGPU.Cards) ? tmpGPU.Cards : [];
		if (tmpCards.length)
		{
			// Name each card (and its VRAM), so an operator can scope which models a box can run, not just a pooled total.
			tmpParts.push('GPU: ' + tmpCards.map((pCard) => String(pCard.Name || 'GPU') + ((pCard.VRAMMB !== null && pCard.VRAMMB !== undefined) ? (' (' + pCard.VRAMMB + ' MB)') : '')).join(', '));
		}
		else
		{
			tmpParts.push('GPU' + ((tmpGPU.VRAMMB !== null && tmpGPU.VRAMMB !== undefined) ? (' ' + tmpGPU.VRAMMB + ' MB VRAM') : ''));
		}
	}
	if (tmpHardware.DiskFreeMB !== null && tmpHardware.DiskFreeMB !== undefined) { tmpParts.push(tmpHardware.DiskFreeMB + ' MB disk free'); }
	return tmpParts.join(', ') || '-';
}
function _advertisesSummary(pRecord)
{
	let tmpCap = _cap(pRecord);
	let tmpCapabilities = Array.isArray(tmpCap.Capabilities) ? tmpCap.Capabilities : [];
	let tmpParts = tmpCapabilities.map((pEntry) =>
	{
		let tmpName = String((pEntry && pEntry.Capability) || '').trim();
		let tmpActions = (pEntry && Array.isArray(pEntry.Actions)) ? pEntry.Actions.map((pAction) => String(pAction || '').trim()).filter((pAction) => pAction) : [];
		if (!tmpName) { return ''; }
		return tmpName + (tmpActions.length ? (' [' + tmpActions.join(', ') + ']') : '');
	}).filter((pPart) => pPart);
	return tmpParts.join('; ') || '-';
}

// Pending | Active | Suspended | Revoked -> a coloured pill (markup; Header.Title and FieldGroups Value tolerate it).
function _statusPill(pStatus)
{
	let tmpStatus = String(pStatus || 'Pending');
	return '<span class="node-status-pill node-status-' + tmpStatus.toLowerCase() + '">' + tmpStatus + '</span>';
}

class NodeWorkspaceProvider extends libWorkspace.PictSectionWorkspaceProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._lastRevealedToken = '';
		this._agent = null;
		this._agentsRoster = [];
		this._provisionMode = 'new';
		this._selectedAgentID = 0;
		this._wantID = 0;
	}

	// Stale-load guard: two overlapping loads (an action's reload racing a navigation to another node) resolve in
	// fetch-completion order, so an older load could paint the node the operator already left. Track the id the
	// newest load wants, and drop any render whose loaded record is not that id (create mode always renders).
	loadAsync(pID, pTab, pMode) { this._wantID = Number(pID) || 0; return super.loadAsync(pID, pTab, pMode); }
	startCreateAsync(pArg) { this._wantID = 0; return super.startCreateAsync(pArg); }
	renderWorkspace()
	{
		if (this._mode !== 'create' && this._wantID && this._record && Number(this._record[this.idField]) !== this._wantID) { return; }
		return super.renderWorkspace();
	}

	// --- REST bridge: the base helpers assume plansheet's Fable RestClient callback shape, which differs; route
	// all three through the promise-based, 401-recovery-covered FrameworkClient instead. Match the base's return
	// shapes: _getJSON resolves the body (or null), _postJSON/_putJSON resolve { error, result }.
	_getJSON(pURL) { return libFrameworkClient.getJSON(this.pict, pURL).catch(() => null); }
	_postJSON(pURL, pBody) { return libFrameworkClient.postJSON(this.pict, pURL, pBody || {}).then((pR) => ({ error: null, result: pR })).catch((pE) => ({ error: pE })); }
	_putJSON(pURL, pBody) { return libFrameworkClient.putJSON(this.pict, pURL, pBody || {}).then((pR) => ({ error: null, result: pR })).catch((pE) => ({ error: pE })); }

	_modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType) { let tmpModal = this._modal(); if (tmpModal && tmpModal.toast) { tmpModal.toast(pMessage, { type: pType || 'success' }); } }

	// The bound agent's display name, resolved by hydrateAsync; used in the subtitle and the overview.
	agentName() { return this._agent ? (this._agent.DisplayName || ('User #' + this._agent.IDUser)) : '-'; }

	// Runs after the record loads, before first paint. Resolve the bound agent for display (cosmetic; silent on
	// failure), and load the account's plan sheets so the Plan sheets grid can render synchronously.
	async hydrateAsync(pRecord)
	{
		this._agent = null;
		this._availableTenants = [];
		let tmpTenants = await this._getJSON('/1.0/Account/Tenants');
		this._availableTenants = (tmpTenants && Array.isArray(tmpTenants.Tenants)) ? tmpTenants.Tenants : [];
		let tmpIDAgent = (pRecord && (pRecord.IDAgentUser || pRecord.IDUser)) || 0;
		if (!(tmpIDAgent > 0)) { return; }
		let tmpAgents = await this._getJSON('/1.0/Node/Agents');
		let tmpList = (tmpAgents && Array.isArray(tmpAgents.Agents)) ? tmpAgents.Agents : [];
		this._agent = tmpList.find((pAgent) => Number(pAgent.IDUser) === Number(tmpIDAgent)) || null;
	}

	// --- plan sheets (which plan sheets this node is authorized to act in) -------------------------------------
	// A node works across MANY plan sheets from ONE registration (WI #470). This is the mirror of the OAuth
	// Connections screen: tick a plan sheet to authorize the node for it, untick to take it away, applied
	// immediately over PUT /1.0/NodeRegistration/:id/Plansheets. The node is never touched.

	// The node's authorized plan sheets, parsed off the record's Customers JSON column. Ownerless model (WI #506):
	// this is EXACTLY the grant set -- the managed-by tenant (IDCustomer) is NOT auto-included; a node acts only
	// where it is explicitly granted.
	_nodeCustomers(pRecord)
	{
		let tmpRecord = pRecord || this._record || {};
		let tmpSet = [];
		try { tmpSet = JSON.parse(tmpRecord.Customers || '[]').map((pV) => parseInt(pV, 10)).filter((pN) => pN > 0); }
		catch (pError) { tmpSet = []; }
		return tmpSet;
	}

	// The checkbox grid, rendered from the loaded tenants + the node's current set. Returned as a FieldGroup
	// Value, so it rides the same markup-permissive path the Capability fields use; tenant names are escaped.
	renderPlansheetGrid(pRecord)
	{
		let tmpRecord = pRecord || this._record || {};
		let tmpManaged = parseInt(tmpRecord.IDCustomer, 10) || 0;
		let tmpSet = this._nodeCustomers(tmpRecord);
		let tmpChecked = {};
		tmpSet.forEach((pID) => { tmpChecked[pID] = true; });
		let tmpTenants = this._availableTenants || [];
		if (!tmpTenants.length) { return '<em>No plan sheets you are a member of.</em>'; }
		let tmpBoxes = tmpTenants.map((pTenant) =>
		{
			let tmpID = parseInt(pTenant.IDCustomer, 10) || 0;
			let tmpIsManaged = (tmpID === tmpManaged);
			let tmpOn = !!tmpChecked[tmpID];
			let tmpName = this._esc(pTenant.CustomerName || pTenant.DisplayName || ('Plan sheet ' + tmpID));
			return ''
				+ '<label class="nw-ps-check"><input type="checkbox" ' + (tmpOn ? 'checked' : '')
				+ ' onchange="_Pict.providers.NodeWorkspace.toggleNodePlansheet(' + tmpID + ', this.checked)" />'
				+ '<span>' + tmpName + (tmpIsManaged ? ' <em>(managed-by)</em>' : '') + '</span></label>';
		}).join('');
		return '<div class="nw-ps-grid">' + tmpBoxes + '</div>';
	}

	// Auto-save per toggle: send the WHOLE set, never a delta, so the screen and the server cannot drift on a
	// dropped request. On success repaint from the returned set; on failure re-load so no checkbox lies.
	async toggleNodePlansheet(pIDCustomer, pEnabled)
	{
		let tmpID = parseInt(pIDCustomer, 10) || 0;
		if (!tmpID) { return; }
		let tmpSet = this._nodeCustomers();
		if (pEnabled) { if (tmpSet.indexOf(tmpID) < 0) { tmpSet.push(tmpID); } }
		else { tmpSet = tmpSet.filter((pID) => pID !== tmpID); }
		let tmpResult = await this._putJSON('/1.0/NodeRegistration/' + this.idRecord + '/Plansheets', { Plansheets: tmpSet });
		if (tmpResult.error)
		{
			this._toast((tmpResult.error && tmpResult.error.message) || 'Could not update the plan sheets.', 'error');
			await this.loadAsync(this.idRecord, this._activeTab, 'view');
			return;
		}
		let tmpFinal = (tmpResult.result && Array.isArray(tmpResult.result.Plansheets)) ? tmpResult.result.Plansheets : tmpSet;
		if (this._record) { this._record.Customers = JSON.stringify(tmpFinal); }
		this._toast('Plan sheets updated.');
		this.renderWorkspace();
	}

	// --- lifecycle actions (Header.Actions OnClick -> these; the server gates each on content.approve) ---------
	async _lifecycle(pAction, pMessage)
	{
		let tmpResult = await this._postJSON('/1.0/NodeRegistration/' + this.idRecord + '/' + pAction, {});
		if (tmpResult.error) { this._toast((tmpResult.error && tmpResult.error.message) || ('Could not ' + pAction.toLowerCase() + ' the node.'), 'error'); return; }
		this._toast(pMessage);
		await this.loadAsync(this.idRecord, this._activeTab, 'view');
	}
	approveNode() { return this._lifecycle('Approve', 'Node approved.'); }
	suspendNode() { return this._lifecycle('Suspend', 'Node suspended.'); }
	resumeNode() { return this._lifecycle('Resume', 'Node resumed.'); }

	// Move this node to another plan sheet's management (WI #506). A picker of the plan sheets the operator belongs
	// to; on confirm the server moves the registration + its agent persona + bound token(s) together. The node then
	// lives in the target tenant's Nodes list, so this view returns to the list on success.
	async changeManagedByAsync()
	{
		let tmpModal = this._modal();
		if (!tmpModal || !tmpModal.show) { return; }
		let tmpRecord = this._record || {};
		let tmpCurrent = parseInt(tmpRecord.IDCustomer, 10) || 0;
		let tmpTenants = (this._availableTenants || []).filter((pTenant) => (parseInt(pTenant.IDCustomer, 10) || 0) > 0);
		if (!tmpTenants.length) { this._toast('You are not a member of another plan sheet to move this node to.', 'error'); return; }
		let tmpOptions = tmpTenants.map((pTenant) =>
		{
			let tmpID = parseInt(pTenant.IDCustomer, 10) || 0;
			let tmpName = this._esc(pTenant.CustomerName || pTenant.DisplayName || ('Plan sheet ' + tmpID));
			return '<option value="' + tmpID + '"' + (tmpID === tmpCurrent ? ' selected' : '') + '>' + tmpName + (tmpID === tmpCurrent ? ' (current)' : '') + '</option>';
		}).join('');
		let tmpContent = ''
			+ '<div class="nw-managedby-modal">'
			+ '<p>Choose the plan sheet that should manage this node. This changes which team administers and sees it -- not where it may act (that is its authorized plan sheets, set separately).</p>'
			+ '<select id="nw-managedby-select" class="nw-input">' + tmpOptions + '</select>'
			+ '</div>';
		let tmpChoice = await tmpModal.show({ title: 'Change managed-by', closeable: true, content: tmpContent,
			buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'move', Label: 'Move', Style: 'primary' } ] });
		if (tmpChoice !== 'move') { return; }
		let tmpEl = document.getElementById('nw-managedby-select');
		let tmpTarget = tmpEl ? (parseInt(tmpEl.value, 10) || 0) : 0;
		if (!tmpTarget || tmpTarget === tmpCurrent) { return; }
		let tmpResult = await this._putJSON('/1.0/NodeRegistration/' + this.idRecord + '/ManagedBy', { IDCustomer: tmpTarget });
		if (tmpResult.error) { this._toast((tmpResult.error && tmpResult.error.message) || 'Could not move the node.', 'error'); return; }
		let tmpPicked = tmpTenants.find((pTenant) => (parseInt(pTenant.IDCustomer, 10) || 0) === tmpTarget) || {};
		this._toast('Node moved to ' + (tmpPicked.CustomerName || tmpPicked.DisplayName || ('plan sheet ' + tmpTarget)) + '.');
		this.backToList();
	}

	async revokeNode()
	{
		let tmpModal = this._modal();
		let tmpOK = (tmpModal && tmpModal.confirm)
			? await tmpModal.confirm('Revoke this node? Its token stops working immediately and it can never reconnect. The agent persona is kept.', { title: 'Revoke node?', confirmLabel: 'Revoke', cancelLabel: 'Cancel', dangerous: true })
			: true;
		if (!tmpOK) { return; }
		return this._lifecycle('Revoke', 'Node revoked; its token is invalidated.');
	}

	async rotateNode()
	{
		let tmpResult = await this._postJSON('/1.0/NodeRegistration/' + this.idRecord + '/Rotate', {});
		if (tmpResult.error) { this._toast((tmpResult.error && tmpResult.error.message) || 'Could not rotate the node token.', 'error'); return; }
		this._revealTokenOnce(tmpResult.result && tmpResult.result.Token, 'Rotated node token');
		await this.loadAsync(this.idRecord, this._activeTab, 'view');
	}

	// --- create / provision ------------------------------------------------------------------------------------
	// base startCreateAsync awaits this before first paint; seed the form state + load the reassign roster.
	async hydrateCreateAsync()
	{
		this._record = { NodeHost: '', Label: '' };
		this._provisionMode = 'new';
		this._selectedAgentID = 0;
		let tmpAgents = await this._getJSON('/1.0/Node/Agents');
		this._agentsRoster = (tmpAgents && Array.isArray(tmpAgents.Agents)) ? tmpAgents.Agents : [];
	}

	// Toggle the two blocks by the hidden attribute (never a repaint -- a repaint would drop the typed label).
	setProvisionMode(pMode)
	{
		this._provisionMode = (pMode === 'existing') ? 'existing' : 'new';
		let tmpNew = document.getElementById('nw-new-block');
		let tmpExisting = document.getElementById('nw-existing-block');
		if (tmpNew) { tmpNew.hidden = (this._provisionMode !== 'new'); }
		if (tmpExisting) { tmpExisting.hidden = (this._provisionMode !== 'existing'); }
	}

	selectProvisionAgent(pIDUser) { this._selectedAgentID = parseInt(pIDUser, 10) || 0; }

	// The create body (EditContent kind:'render'); the base assigns the returned string into the panel.
	renderProvisionForm()
	{
		let tmpHasAgents = (this._agentsRoster || []).length > 0;
		let tmpOptions = (this._agentsRoster || []).map((pAgent) =>
			'<option value="' + pAgent.IDUser + '">' + this._esc(pAgent.DisplayName || ('User #' + pAgent.IDUser))
				+ ' (' + (pAgent.ActiveNodeCount || 0) + ' active)</option>').join('');
		return ''
			+ '<div class="nw-cli-callout">'
			+ '<p class="nw-cli-lead">Recommended: connect a runner from its own machine with the CLI. One command logs in, provisions, mints a machine key, and joins the hub.</p>'
			+ '<code class="nw-cli-cmd">plansheet-node login --url &lt;plansheet-url&gt; --hub &lt;hub-url&gt; --name &lt;node-name&gt;</code>'
			+ '<p class="nw-cli-note">Add <code>--managed-by &lt;plan sheet #&gt;</code> and <code>--grant &lt;plan sheet #&gt;</code> to target a specific plan sheet.</p>'
			+ '</div>'
			+ '<p class="nw-advanced-heading">Advanced / manual</p>'
			+ '<p class="nw-advanced-note">Create a registration here and paste its token into a hand-configured runner. A node made this way has no machine key, so it stays offline (no hub beacon) until a runner registers one with the token. For a normal runner, use the CLI above.</p>'
			+ '<div class="pw-panel-standalone"><div class="pw-group">'
			+ '<p class="pw-field"><label><input type="radio" name="nw-mode" value="new" checked onchange="_Pict.providers.NodeWorkspace.setProvisionMode(\'new\')"> Create a new agent</label>'
			+ '&nbsp;&nbsp;<label><input type="radio" name="nw-mode" value="existing"' + (tmpHasAgents ? '' : ' disabled')
			+ ' onchange="_Pict.providers.NodeWorkspace.setProvisionMode(\'existing\')"> Reassign an existing agent</label></p>'
			+ '<div id="nw-new-block" class="pw-field"><label>Agent name</label>'
			+ '<input id="nw-agent-name" class="nw-input" type="text" placeholder="e.g. pop-os runner" '
			+ 'onkeydown="if(event.key===\'Enter\'){_Pict.providers.NodeWorkspace.submitProvisionAsync();}"></div>'
			+ '<div id="nw-existing-block" class="pw-field" hidden><label>Existing agent</label>'
			+ '<select class="nw-input" onchange="_Pict.providers.NodeWorkspace.selectProvisionAgent(this.value)">'
			+ '<option value="0">Select an agent...</option>' + tmpOptions + '</select></div>'
			+ '<div class="pw-field"><label>Label (optional)</label><input id="nw-label" class="nw-input" type="text" placeholder="node runner"></div>'
			+ '</div></div>';
	}

	async submitProvisionAsync()
	{
		let tmpLabelEl = document.getElementById('nw-label');
		let tmpLabel = tmpLabelEl ? String(tmpLabelEl.value || '').trim() : '';
		let tmpBody = {};
		if (tmpLabel) { tmpBody.Label = tmpLabel; }
		if (this._provisionMode === 'existing')
		{
			if (!(this._selectedAgentID > 0)) { this._toast('Choose an existing agent to reassign.', 'error'); return; }
			tmpBody.IDAgentUser = this._selectedAgentID;
		}
		else
		{
			let tmpNameEl = document.getElementById('nw-agent-name');
			let tmpName = tmpNameEl ? String(tmpNameEl.value || '').trim() : '';
			if (!tmpName) { this._toast('Enter a name for the new agent.', 'error'); return; }
			tmpBody.AgentName = tmpName;
		}
		let tmpResult = await this._postJSON('/1.0/Node/Provision', tmpBody);
		if (tmpResult.error) { this._toast((tmpResult.error && tmpResult.error.message) || 'Could not provision the node.', 'error'); return; }
		let tmpNode = tmpResult.result || {};
		this._revealTokenOnce(tmpNode.Token, 'Node registration token');
		let tmpID = tmpNode.NodeRegistration && tmpNode.NodeRegistration.IDNodeRegistration;
		if (tmpID) { window.location.hash = '#/node/' + tmpID; }
		else { this.backToList(); }
	}

	// --- token reveal (shared by Provision and Rotate) ---------------------------------------------------------
	// A node token, like an API token, is shown exactly once. The classes live in the config CSSExtra so this
	// renders styled even if the Settings token surface was never opened.
	_revealTokenOnce(pToken, pTitle)
	{
		this._lastRevealedToken = String(pToken || '');
		let tmpModal = this._modal();
		if (!tmpModal || !tmpModal.show) { return; }
		let tmpContent = ''
			+ '<div class="cst-token-reveal">'
			+ '<p class="cst-token-reveal-warn">Copy this token now. It is shown only once and cannot be retrieved again.</p>'
			+ '<div class="cst-token-value-row"><code id="NodeWorkspace-Token-Reveal" class="cst-token-value">' + this._esc(this._lastRevealedToken) + '</code>'
			+ '<button class="cst-token-copy-btn" onclick="_Pict.providers.NodeWorkspace.copyRevealedToken()">Copy</button></div>'
			+ '<p class="cst-token-reveal-hint">The node presents this as a bearer token (Authorization: Bearer ...). A CLI runner (plansheet-node) mints and stores its own token, so you only need this for a hand-configured runner -- and such a node stays offline until a runner registers a machine key with it.</p>'
			+ '</div>';
		tmpModal.show({ title: pTitle || 'Node token', closeable: true, content: tmpContent, buttons: [ { Hash: 'done', Label: 'Done', Style: 'primary' } ] });
	}

	copyRevealedToken()
	{
		let tmpToken = this._lastRevealedToken || '';
		if (typeof navigator !== 'undefined' && navigator.clipboard && tmpToken) { navigator.clipboard.writeText(tmpToken); }
		let tmpBtn = document.querySelector('.cst-token-copy-btn');
		if (tmpBtn) { tmpBtn.textContent = 'Copied'; }
		// A node credential should not linger in memory once copied.
		this._lastRevealedToken = '';
	}
}

const NodeWorkspaceConfig =
{
	// Identifier MUST be a bare JS identifier: the base emits inline handlers as _Pict.providers.<Identifier>.<m>(),
	// so a hyphen would render invalid JS.
	Identifier: 'NodeWorkspace',
	Ordinal: 230,
	DestinationAddress: '#Plansheet-Workspace',   // plansheet's shell center content region (PictView-Shell)
	ProviderClass: NodeWorkspaceProvider,
	Entity:
	{
		Name: 'NodeRegistration', IDField: 'IDNodeRegistration', Collection: 'NodeRegistrations', GUIDField: 'GUIDNodeRegistration',
		ReadEndpoint: (pID) => '/1.0/NodeRegistration/' + pID
	},
	Permissions: { Read: () => true },   // fail-open read; every mutation is content.approve-gated server-side
	Header:
	{
		Eyebrow: 'Node',
		Title: (pRec, pProv) => (pProv.mode === 'create')
			? 'Provision a node'
			: (_statusPill(pRec.Status) + ' ' + pProv._esc(pRec.NodeHost || ('Node #' + (pRec.NodeRegistrationNumber || pRec.IDNodeRegistration)))),
		Subtitle: (pRec, pProv) => (pProv.mode === 'create') ? ''
			: ('Agent ' + pProv._esc(pProv.agentName()) + (pRec.LastSeenDate ? (' - last seen ' + String(pRec.LastSeenDate).slice(0, 16).replace('T', ' ')) : '')),
		ListRoute: '#/node-registrations',
		Actions:
		[
			{ Label: 'Provision node', Icon: 'fas fa-plus', Style: 'is-link', OnClick: '#/node/new', When: (pProv) => pProv.mode === 'view' },
			{ Label: 'Approve', Icon: 'fas fa-check', OnClick: 'approveNode()', When: (pProv) => pProv.mode === 'view' && pProv.record.Status === 'Pending' },
			{ Label: 'Suspend', Icon: 'fas fa-pause', OnClick: 'suspendNode()', When: (pProv) => pProv.mode === 'view' && pProv.record.Status === 'Active' },
			{ Label: 'Resume', Icon: 'fas fa-play', OnClick: 'resumeNode()', When: (pProv) => pProv.mode === 'view' && pProv.record.Status === 'Suspended' },
			{ Label: 'Rotate token', Icon: 'fas fa-rotate', OnClick: 'rotateNode()', When: (pProv) => pProv.mode === 'view' && (pProv.record.Status === 'Active' || pProv.record.Status === 'Suspended') },
			{ Label: 'Managed by...', Icon: 'fas fa-right-left', OnClick: 'changeManagedByAsync()', When: (pProv) => pProv.mode === 'view' && pProv.record.Status && pProv.record.Status !== 'Revoked' },
			{ Label: 'Revoke', Icon: 'fas fa-ban', Style: 'is-danger', OnClick: 'revokeNode()', When: (pProv) => pProv.mode === 'view' && pProv.record.Status && pProv.record.Status !== 'Revoked' },
			{ Label: 'Provision', Icon: 'fas fa-check', Style: 'is-link', OnClick: 'submitProvisionAsync()', When: (pProv) => pProv.mode === 'create' },
			{ Label: 'Cancel', Icon: 'fas fa-xmark', Style: 'is-light', OnClick: 'backToList()', When: (pProv) => pProv.isEditing }
		]
	},
	Tabs: [ { Key: 'overview', Label: 'Overview', Icon: 'fas fa-circle-info', Content: { kind: 'overview' } } ],
	FieldGroups:
	[
		{ Name: 'Status', Fields:
			[
				{ Key: 'Status', Label: 'Status', Value: (pRec) => _statusPill(pRec.Status) },
				{ Key: 'LastSeenDate', Label: 'Last seen', Value: (pRec) => pRec.LastSeenDate ? String(pRec.LastSeenDate).slice(0, 16).replace('T', ' ') : '-' }
			] },
		{ Name: 'Identity', Fields:
			[
				// NodeHost and NodeKey are node-reported (Self/Register), so they MUST be escaped: the base overview
				// renderer emits a bare field value through a markup-permissive path, so an unescaped value carrying
				// '<...' would inject into the admin's page. Escape them like the Capability fields below.
				{ Key: 'NodeHost', Label: 'Host', Value: (pRec, pProv) => pProv._esc(pRec.NodeHost || '-') },
				{ Key: 'NodeKey', Label: 'Node key', Mono: true, Value: (pRec, pProv) => pProv._esc(pRec.NodeKey || '-') },
				{ Key: 'NodeRegistrationNumber', Label: '#' },
				{ Key: 'IDAgentUser', Label: 'Bound agent', Value: (pRec, pProv) => pProv._esc(pProv.agentName()) }
			] },
		{ Name: 'Capability', Fields:
			[
				{ Key: 'CapabilityJSON', Label: 'Roles', Value: (pRec, pProv) => pProv._esc(_rolesSummary(pRec)) },
				{ Key: 'CapabilityJSON', Label: 'Workspaces', Wide: true, Value: (pRec, pProv) => pProv._esc(_workspacesSummary(pRec)) },
				{ Key: 'CapabilityJSON', Label: 'Advertises', Wide: true, Value: (pRec, pProv) => pProv._esc(_advertisesSummary(pRec)) },
				{ Key: 'CapabilityJSON', Label: 'Client version', Value: (pRec, pProv) => pProv._esc(_versionSummary(pRec)) },
				{ Key: 'CapabilityJSON', Label: 'Hardware', Wide: true, Value: (pRec, pProv) => pProv._esc(_hardwareSummary(pRec)) }
			] },
		// Which plan sheets this node may act in. One node, many plan sheets, granted and revoked live from here
		// with the node never touched (WI #470). The home plan sheet is always ticked and cannot be removed.
		{ Name: 'Plan sheets', Fields:
			[
				{ Key: 'Customers', Label: 'Authorized', Wide: true, Value: (pRec, pProv) => pProv.renderPlansheetGrid(pRec) }
			] }
	],
	EditContent: { kind: 'render', ref: 'renderProvisionForm' },
	CSSExtra:
		'.node-status-pill{display:inline-block;font-size:0.62em;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;padding:2px 9px;border-radius:11px;vertical-align:middle;}'
		+ '.node-status-pending{background:#F6C453;color:#3a2f00}.node-status-active{background:#2E7D32;color:#fff}.node-status-suspended{background:#9e9e9e;color:#fff}.node-status-revoked{background:#C62828;color:#fff}'
		+ '.cst-token-reveal{display:flex;flex-direction:column;gap:10px}.cst-token-reveal-warn{margin:0;font-size:0.9em;color:var(--theme-color-status-error,#c0353a)}'
		+ '.cst-token-value-row{display:flex;gap:8px;align-items:stretch}.cst-token-value{flex:1;min-width:0;padding:8px 10px;background:var(--theme-color-background-secondary,#f3f5f9);border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;font-family:ui-monospace,Menlo,monospace;font-size:0.85em;word-break:break-all;color:var(--theme-color-text-primary,#1d2230)}'
		+ '.cst-token-copy-btn{padding:6px 14px;border:1px solid var(--theme-color-brand-primary,#3b6fd4);border-radius:6px;background:var(--theme-color-brand-primary,#3b6fd4);color:#fff;cursor:pointer;font-size:0.85em;white-space:nowrap}'
		+ '.cst-token-reveal-hint{margin:0;font-size:0.8em;color:var(--theme-color-text-secondary,#5b6376);line-height:1.5}'
		+ '.nw-cli-callout{margin:0 0 16px;padding:12px 14px;background:var(--theme-color-background-secondary,#f3f5f9);border:1px solid var(--theme-color-border-default,#d8dde6);border-left:3px solid var(--theme-color-brand-primary,#3b6fd4);border-radius:6px;display:flex;flex-direction:column;gap:8px}'
		+ '.nw-cli-lead{margin:0;font-size:0.9em;color:var(--theme-color-text-primary,#1d2230)}'
		+ '.nw-cli-cmd{display:block;padding:8px 10px;background:var(--theme-color-background,#fff);border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:5px;font-family:ui-monospace,Menlo,monospace;font-size:0.82em;color:var(--theme-color-text-primary,#1d2230);word-break:break-all}'
		+ '.nw-cli-note{margin:0;font-size:0.8em;color:var(--theme-color-text-secondary,#5b6376)}.nw-cli-note code{font-family:ui-monospace,Menlo,monospace;font-size:0.95em}'
		+ '.nw-advanced-heading{margin:0 0 4px;font-size:0.82em;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--theme-color-text-secondary,#5b6376)}'
		+ '.nw-advanced-note{margin:0 0 12px;font-size:0.82em;color:var(--theme-color-text-secondary,#5b6376);line-height:1.5}'
		+ '.nw-managedby-modal p{margin:0 0 12px;font-size:0.9em;line-height:1.5;color:var(--theme-color-text-secondary,#5b6376)}'
		+ '.nw-input{width:100%;padding:8px 10px;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;background:var(--theme-color-background-secondary,#f3f5f9);color:var(--theme-color-text-primary,#1d2230)}'
		+ '.nw-ps-grid{display:flex;flex-direction:column;gap:0.35rem}'
		+ '.nw-ps-check{display:flex;align-items:center;gap:0.5rem;font-size:0.9em;color:var(--theme-color-text-primary,#1d2230)}'
		+ '.nw-ps-check input{margin:0}.nw-ps-check em{color:var(--theme-color-text-secondary,#5b6376);font-style:italic}'
		+ '.nw-ps-check code{background:var(--theme-color-background-secondary,#f3f5f9);padding:1px 5px;border-radius:4px;font-size:0.85em}'
};

function registerNodeWorkspace(pPict)
{
	libWorkspace.createWorkspace(pPict, NodeWorkspaceConfig);
	return NodeWorkspaceConfig;
}

module.exports = { NodeWorkspaceConfig: NodeWorkspaceConfig, NodeWorkspaceProvider: NodeWorkspaceProvider, registerNodeWorkspace: registerNodeWorkspace };
